import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, hint, error, required, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-tabaco mb-1.5">
        {label} {required && <span className="text-cobre">*</span>}
      </span>
      {children}
      {hint && !error && <span className="block text-2xs text-ink-subtle mt-1">{hint}</span>}
      {error && <span className="block text-2xs text-cobre-700 mt-1">{error}</span>}
    </label>
  );
}

const inputBase =
  'block w-full rounded-md border border-areia bg-cream/60 px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-subtle focus:outline-none focus:border-floresta focus:bg-cream ' +
  'transition-colors';

type TextInputProps = InputHTMLAttributes<HTMLInputElement>;

export function TextInput(props: TextInputProps) {
  return <input {...props} className={`${inputBase} ${props.className ?? ''}`} />;
}

type SelectInputProps = SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode };

export function SelectInput({ children, ...props }: SelectInputProps) {
  return (
    <select {...props} className={`${inputBase} ${props.className ?? ''}`}>
      {children}
    </select>
  );
}

interface RadioOptionProps {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  label: string;
  hint?: string;
}

// Radio chips em grid — UI canônica das opções de transport_mode / lodging_type.
export function RadioChip({ name, value, checked, onChange, label, hint }: RadioOptionProps) {
  return (
    <label
      className={
        'flex flex-col gap-0.5 px-3 py-2.5 rounded-md border cursor-pointer transition-colors ' +
        (checked
          ? 'border-floresta bg-floresta/5 text-floresta'
          : 'border-areia bg-cream/40 text-ink hover:border-cobre/60')
      }
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="text-2xs text-ink-subtle">{hint}</span>}
    </label>
  );
}
